/**
 * StaffingIntelligencePanel — self-explaining AI callout for a shift.
 *
 * Renders the category chip, one-sentence headline, one-sentence
 * explanation, and (optionally) the top 2 recommended actions from the
 * AIOpsEngine. Meant to sit inside the existing shift/event cards so
 * operators never have to interpret raw numbers.
 */
import React from 'react';
import { TONE_STYLES } from '../utils/aiOpsEngine';
import { Button } from './ui/button';

export default function StaffingIntelligencePanel({
  intel, onAction, compact = false, showActions = true,
}) {
  if (!intel) return null;
  const style = TONE_STYLES[intel.tone] || TONE_STYLES.neutral;
  const actions = (intel.actions || []).slice(0, compact ? 1 : 3);

  return (
    <div
      className={`mt-2 rounded-lg border ${style.card} px-3 py-2`}
      data-testid={`intel-panel-${intel.category}`}
    >
      <div className="flex items-start gap-2">
        <span
          className={`shrink-0 mt-0.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border ${style.chip}`}
          data-testid={`intel-chip-${intel.category}`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
          {intel.categoryLabel}
        </span>
        <div className="min-w-0 flex-1">
          <div
            className={`text-sm font-semibold ${style.text}`}
            data-testid={`intel-headline-${intel.category}`}
          >
            {intel.headline}
          </div>
          {intel.explanation && !compact && (
            <div className="text-xs text-slate-600 mt-0.5">{intel.explanation}</div>
          )}
        </div>
      </div>
      {showActions && actions.length > 0 && !compact && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {actions.map((a) => (
            <Button
              key={a.id}
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                onAction?.(a);
              }}
              data-testid={a.testid}
            >
              {a.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
